import { BatteryChargingFullOutlined, Bolt, LocalShippingOutlined, LocationOnOutlined, SettingsOutlined } from '@mui/icons-material'
import { useTranslations } from 'next-intl'
import { useContext, useMemo } from 'react'

import { AbilityContext } from '~/layouts/components/acl/Can'
import routeDirectory from '~/navigation/routes'
import useNavItems from '~/navigation/vertical'

import { ROUTE_TO_ALIAS_KEY } from '../config/pageAliasKeys'
import type { QuickSearchPage } from '../types'
import flattenNavItems from '../utils/flattenNavItems'
import usePageAliases from './usePageAliases'

/**
 * Manage views are not standalone routes but query-string variants of the corresponding
 * overview pages (e.g. `/chargers?mode=manage`). They are surfaced as explicit Cmd+K
 * entries gated by the same ACL the manage toggle uses on each page.
 */
const MANAGE_PAGES = [
  {
    labelKey: 'manageBatteries',
    aliasKey: 'manageBatteries',
    href: `${routeDirectory.batteries.root}?mode=manage`,
    action: 'update',
    subject: 'site',
    icon: <BatteryChargingFullOutlined fontSize="small" />
  },
  {
    labelKey: 'manageChargers',
    aliasKey: 'manageChargers',
    href: `${routeDirectory.chargers.root}?mode=manage`,
    action: 'update',
    subject: 'charger',
    icon: <Bolt fontSize="small" />
  },
  {
    labelKey: 'manageSites',
    aliasKey: 'manageSites',
    href: `${routeDirectory.sites.root}?mode=manage`,
    action: 'update',
    subject: 'site',
    icon: <LocationOnOutlined fontSize="small" />
  },
  {
    labelKey: 'manageVehicles',
    aliasKey: 'manageVehicles',
    href: `${routeDirectory.vehicles.root}?mode=manage`,
    action: 'update',
    subject: 'vehicle',
    icon: <LocalShippingOutlined fontSize="small" />
  }
] as const

export default function useQuickSearchPages(): QuickSearchPage[] {
  const t = useTranslations('quickSearch.pages')
  const navItems = useNavItems()
  const ability = useContext(AbilityContext)
  const aliases = usePageAliases()

  return useMemo(() => {
    const allNavLinks = flattenNavItems(navItems)

    // Transform to QuickSearchPage format and filter on permissions
    // Some pages have already been filtered out in useNavItems based on org level
    // flags, but we still need to filter out based on ACL permissions
    const pages: QuickSearchPage[] = []

    // Add the user's own settings page - this is accessible to all users
    // but needs to be added specifically because it is not in main nav.
    pages.push({
      id: routeDirectory.settings.root,
      label: t('accountSettings'),
      href: routeDirectory.settings.root,
      icon: <SettingsOutlined fontSize="small" />,
      keywords: aliases.accountSettings
    })

    // Manage views live behind a query string on the overview pages, so they have no
    // entry in the sidebar. Push them explicitly so users can jump straight in.
    for (const manage of MANAGE_PAGES) {
      if (ability && !ability.can(manage.action, manage.subject)) continue
      pages.push({
        id: manage.href,
        label: t(manage.labelKey),
        href: manage.href,
        icon: manage.icon,
        keywords: aliases[manage.aliasKey]
      })
    }

    for (const navLink of allNavLinks) {
      const aliasKey = ROUTE_TO_ALIAS_KEY[navLink.path]
      const keywords = aliasKey ? aliases[aliasKey] : undefined

      // Check permissions - items with auth: false are always visible
      if (navLink.auth === false) {
        pages.push({
          id: navLink.path,
          label: navLink.title,
          href: navLink.path,
          subLabel: navLink.breadcrumb,
          icon: navLink.icon,
          keywords
        })
        continue
      }

      // Check if user has permission to view this link
      if (navLink.action && navLink.subject) {
        if (ability && ability.can(navLink.action, navLink.subject)) {
          pages.push({
            id: navLink.path,
            label: navLink.title,
            href: navLink.path,
            subLabel: navLink.breadcrumb,
            icon: navLink.icon,
            keywords
          })
        }
      } else if (!navLink.action && !navLink.subject) {
        // No action/subject means no permission control, so add it
        pages.push({
          id: navLink.path,
          label: navLink.title,
          href: navLink.path,
          subLabel: navLink.breadcrumb,
          icon: navLink.icon,
          keywords
        })
      }
    }

    return pages
  }, [navItems, ability, t, aliases])
}
